import { supabase } from '@/lib/supabase'
import type {
  Candidate,
  CandidateWithParty,
  CandidateWithDistrict,
  CandidateWithDetails,
  CreateCandidateInput,
  UpdateCandidateStatsInput,
  UpdateCandidateAssignmentInput,
  CandidateRole,
} from '@/types'

// Query key factory for consistent cache management
export const candidateKeys = {
  all: ['candidates'] as const,
  byParty: (partyId: string) => ['candidates', 'party', partyId] as const,
  activeByParty: (partyId: string) => ['candidates', 'party', partyId, 'active'] as const,
  detail: (id: string) => ['candidates', 'id', id] as const,
  detailWithParty: (id: string) => ['candidates', 'detail', id] as const,
  detailWithDetails: (id: string) => ['candidates', 'detailFull', id] as const,
  partyLeader: (partyId: string) => ['candidates', 'leader', partyId] as const,
  regionalCandidates: (partyId: string) => ['candidates', 'regional', partyId] as const,
  districtCandidate: (partyId: string, districtId: string) =>
    ['candidates', 'district', partyId, districtId] as const,
}

// Fetch all candidates for a party
export async function fetchCandidatesByParty(partyId: string): Promise<Candidate[]> {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('party_id', partyId)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as Candidate[]
}

// Fetch all active candidates for a party
export async function fetchActiveCandidatesByParty(partyId: string): Promise<Candidate[]> {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('party_id', partyId)
    .eq('is_active', true)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as Candidate[]
}

// Fetch single candidate by id
export async function fetchCandidate(id: string): Promise<Candidate> {
  const { data, error } = await supabase.from('candidates').select('*').eq('id', id).single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Fetch candidate with party details
export async function fetchCandidateWithParty(id: string): Promise<CandidateWithParty> {
  const { data, error } = await supabase
    .from('candidates')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color, ideology_vector)
    `
    )
    .eq('id', id)
    .single()

  if (error) {
    throw error
  }

  return data as CandidateWithParty
}

// Fetch candidate with full details (party and district)
export async function fetchCandidateWithDetails(id: string): Promise<CandidateWithDetails> {
  const { data, error } = await supabase
    .from('candidates')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color, ideology_vector),
      district:districts(
        id,
        name,
        settlement_type,
        population,
        region:regions(id, name)
      )
    `
    )
    .eq('id', id)
    .single()

  if (error) {
    throw error
  }

  return data as CandidateWithDetails
}

// Fetch party leader for a party
export async function fetchPartyLeader(partyId: string): Promise<Candidate | null> {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('party_id', partyId)
    .eq('role', 'party_leader')
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data as Candidate | null
}

// Fetch regional candidates for a party (with district info)
export async function fetchRegionalCandidates(partyId: string): Promise<CandidateWithDistrict[]> {
  const { data, error } = await supabase
    .from('candidates')
    .select(
      `
      *,
      district:districts(
        id,
        name,
        settlement_type,
        population,
        region:regions(id, name)
      )
    `
    )
    .eq('party_id', partyId)
    .eq('role', 'regional_candidate')
    .eq('is_active', true)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as CandidateWithDistrict[]
}

// Fetch candidate assigned to a specific district
export async function fetchDistrictCandidate(
  partyId: string,
  districtId: string
): Promise<Candidate | null> {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('party_id', partyId)
    .eq('assigned_district_id', districtId)
    .eq('role', 'regional_candidate')
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data as Candidate | null
}

// Create candidate (recruitment)
export async function createCandidate(input: CreateCandidateInput): Promise<Candidate> {
  const { data, error } = await supabase.from('candidates').insert(input).select().single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Update candidate (general updates)
export async function updateCandidate(
  id: string,
  updates: UpdateCandidateStatsInput
): Promise<Candidate> {
  const { data, error } = await supabase
    .from('candidates')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Update candidate loyalty (game mechanics)
export async function updateCandidateLoyalty(
  id: string,
  loyaltyChange: number
): Promise<Candidate> {
  // First fetch current loyalty
  const { data: current, error: fetchError } = await supabase
    .from('candidates')
    .select('loyalty')
    .eq('id', id)
    .single()

  if (fetchError) {
    throw fetchError
  }

  // Clamp to 0-100 range
  const newLoyalty = Math.max(0, Math.min(100, (current?.loyalty ?? 50) + loyaltyChange))

  const { data, error } = await supabase
    .from('candidates')
    .update({ loyalty: newLoyalty })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Update candidate recognition (game mechanics)
export async function updateCandidateRecognition(
  id: string,
  recognitionChange: number
): Promise<Candidate> {
  // First fetch current recognition
  const { data: current, error: fetchError } = await supabase
    .from('candidates')
    .select('recognition')
    .eq('id', id)
    .single()

  if (fetchError) {
    throw fetchError
  }

  // Clamp to 0-100 range
  const newRecognition = Math.max(0, Math.min(100, (current?.recognition ?? 10) + recognitionChange))

  const { data, error } = await supabase
    .from('candidates')
    .update({ recognition: newRecognition })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Validate role assignment before executing
export async function validateRoleAssignment(
  candidateId: string,
  partyId: string,
  role: CandidateRole,
  districtId?: string
): Promise<{ valid: boolean; error?: string }> {
  // party_leader: check no other active leader exists
  if (role === 'party_leader') {
    const existingLeader = await fetchPartyLeader(partyId)
    if (existingLeader && existingLeader.id !== candidateId) {
      return { valid: false, error: 'Party already has a leader. Remove current leader first.' }
    }
  }

  // regional_candidate: requires districtId, check no other candidate in that district
  if (role === 'regional_candidate') {
    if (!districtId) {
      return { valid: false, error: 'Regional candidate requires a district assignment.' }
    }
    const existingCandidate = await fetchDistrictCandidate(partyId, districtId)
    if (existingCandidate && existingCandidate.id !== candidateId) {
      return { valid: false, error: 'District already has an assigned candidate.' }
    }
  }

  // minister: will be validated in Phase 4

  return { valid: true }
}

// Assign role to candidate
export async function assignCandidateRole(
  id: string,
  partyId: string,
  role: CandidateRole,
  districtId?: string
): Promise<Candidate> {
  // Validate the assignment first
  const validation = await validateRoleAssignment(id, partyId, role, districtId)
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  const updateData: UpdateCandidateAssignmentInput = { role }

  // Set or clear district assignment based on role
  if (role === 'regional_candidate') {
    updateData.assigned_district_id = districtId
  } else {
    updateData.assigned_district_id = null
  }

  const { data, error } = await supabase
    .from('candidates')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Remove role from candidate (set to 'none')
export async function removeCandidateRole(id: string): Promise<Candidate> {
  const { data, error } = await supabase
    .from('candidates')
    .update({ role: 'none', assigned_district_id: null })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Candidate
}

// Deactivate candidate (leaves party)
export async function deactivateCandidate(id: string): Promise<void> {
  const { error } = await supabase
    .from('candidates')
    .update({ is_active: false, role: 'none', assigned_district_id: null })
    .eq('id', id)

  if (error) {
    throw error
  }
}

// Bulk update candidates (tick processing)
export async function bulkUpdateCandidates(
  updates: Array<{ id: string; loyalty?: number; recognition?: number }>
): Promise<void> {
  // Supabase doesn't support bulk updates with different values per row easily,
  // so we need to do individual updates. For better performance in production,
  // consider using an RPC function.
  const promises = updates.map(({ id, ...data }) => {
    // Clamp values to 0-100 range
    const clampedData: { loyalty?: number; recognition?: number } = {}
    if (data.loyalty !== undefined) {
      clampedData.loyalty = Math.max(0, Math.min(100, data.loyalty))
    }
    if (data.recognition !== undefined) {
      clampedData.recognition = Math.max(0, Math.min(100, data.recognition))
    }

    return supabase.from('candidates').update(clampedData).eq('id', id)
  })

  const results = await Promise.all(promises)

  // Check for any errors
  const errors = results.filter((r) => r.error)
  if (errors.length > 0) {
    throw errors[0].error
  }
}
